import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../AuthContext";
import { API_BASE_URL } from "../../config";
import Header from "../Header";
import {
  format,
  isToday,
  isYesterday,
  parseISO,
  startOfToday,
  endOfToday,
  startOfMonth,
  endOfMonth,
  subMonths,
} from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function Sales() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(endOfToday());

  // Memoize the formatted dates to prevent unnecessary re-renders
  const formattedStartDate = useMemo(
    () => (startDate ? format(startDate, "yyyy-MM-dd") : null),
    [startDate],
  );
  const formattedEndDate = useMemo(
    () => (endDate ? format(endDate, "yyyy-MM-dd") : null),
    [endDate],
  );

  const fetchSalesData = useCallback(async () => {
    if (!formattedStartDate || !formattedEndDate) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/sales`,
        {
          start_date: formattedStartDate,
          end_date: formattedEndDate,
          show_all: true,
        },
        {
          headers: { "Content-Type": "application/json" },
        },
      );

      if (response.data?.data) {
        setSalesData(response.data.data);
      }
    } catch (err) {
      console.error("API Error:", err);
      if (err.response?.status === 401) {
        logout();
        navigate("/login");
      } else {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to fetch sales data",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [formattedStartDate, formattedEndDate, logout, navigate]);

  // Calculate totals only when salesData changes
  const { totals, formattedSales } = useMemo(() => {
    const calculatedTotals = {
      oil_kg: { qty: 0, amount: 0 },
      oil_ltr: { qty: 0, amount: 0 },
      oil_bottle_1l: { qty: 0, amount: 0 },
      oil_bottle_5l: { qty: 0, amount: 0 },
      oil_bottle_15l: { qty: 0, amount: 0 },
      groundnut: { qty: 0, amount: 0 },
      cake: { qty: 0, amount: 0 },
      grand_total: 0,
    };

    const formatted = salesData
      .map((sale) => {
        const saleDate = parseISO(sale.sale_date);
        const isTodaySale = isToday(saleDate);
        const isYesterdaySale = isYesterday(saleDate);

        // Update totals
        if (calculatedTotals[sale.product_type]) {
          calculatedTotals[sale.product_type].qty +=
            parseFloat(sale.quantity) || 0;
          calculatedTotals[sale.product_type].amount +=
            parseFloat(sale.total) || 0;
        }
        calculatedTotals.grand_total += parseFloat(sale.total) || 0;

        return {
          ...sale,
          formattedDate: format(saleDate, "MMM d, yyyy"),
          formattedTime: format(saleDate, "h:mm a"),
          rowClass: isTodaySale
            ? "table-primary"
            : isYesterdaySale
              ? "table-light"
              : "",
        };
      })
      .sort((a, b) => new Date(b.sale_date) - new Date(a.sale_date));

    return { totals: calculatedTotals, formattedSales: formatted };
  }, [salesData]);

  // Initial data load and when date range changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSalesData();
    }, 300); // Small debounce to prevent rapid API calls

    return () => clearTimeout(timer);
  }, [fetchSalesData]);

  const handleDateFilter = (filterType) => {
    const today = new Date();

    switch (filterType) {
      case "today":
        setStartDate(startOfToday());
        setEndDate(endOfToday());
        break;
      case "thisMonth":
        setStartDate(startOfMonth(today));
        setEndDate(endOfMonth(today));
        break;
      case "lastMonth":
        setStartDate(startOfMonth(subMonths(today, 1)));
        setEndDate(endOfMonth(subMonths(today, 1)));
        break;
      default:
        break;
    }
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(num);
  };

  const getProductName = (productType) => {
    const names = {
      oil_kg: "Oil (KG)",
      oil_ltr: "Oil (Liter)",
      oil_bottle_1l: "Oil Bottle (1L)",
      oil_bottle_5l: "Oil Bottle (5L)",
      oil_bottle_15l: "Oil Bottle (15L)",
      groundnut: "Groundnut",
      cake: "Cake",
    };
    return names[productType] || productType;
  };

  const productTypes = [
    { key: "oil_kg", label: "Oil (KG)", color: "primary" },
    { key: "oil_ltr", label: "Oil (Liter)", color: "success" },
    { key: "oil_bottle_1l", label: "1L Bottle", color: "info" },
    { key: "oil_bottle_5l", label: "5L Bottle", color: "warning" },
    { key: "oil_bottle_15l", label: "15L Bottle", color: "danger" },
    { key: "groundnut", label: "Groundnut", color: "secondary" },
    { key: "cake", label: "Cake", color: "dark" },
  ];

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          Error: {error}
          <button
            className="btn btn-sm btn-outline-secondary ms-3"
            onClick={fetchSalesData}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="sales-container">
        <div className="container-fluid py-3">
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h4 className="mb-0">Sales Report</h4>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div className="btn-group btn-group-sm me-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => handleDateFilter("today")}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => handleDateFilter("thisMonth")}
                    >
                      This Month
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => handleDateFilter("lastMonth")}
                    >
                      Last Month
                    </button>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <span className="small text-muted">Start:</span>
                      <DatePicker
                        selected={startDate}
                        onChange={setStartDate}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        maxDate={endDate || new Date()}
                        className="form-control form-control-sm"
                        dateFormat="MMM d, yyyy"
                        placeholderText="Start date"
                      />
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="small text-muted">End:</span>
                      <DatePicker
                        selected={endDate}
                        onChange={setEndDate}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        maxDate={new Date()}
                        className="form-control form-control-sm"
                        dateFormat="MMM d, yyyy"
                        placeholderText="End date"
                      />
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={fetchSalesData}
                      disabled={loading}
                    >
                      {loading ? "Refreshing..." : "Apply"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="card-body border-bottom">
              <div className="row g-3">
                {productTypes.map(({ key, label, color }) => (
                  <div key={key} className="col-6 col-md-4 col-lg-2">
                    <div className="card h-100 border-0 shadow-sm">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="text-muted small mb-1">{label}</h6>
                            <h5 className="mb-0 fw-bold">
                              {totals[key]?.qty || 0}
                            </h5>
                          </div>
                          <div
                            className={`bg-${color}-light bg-opacity-10 p-2 rounded`}
                          >
                            <span className={`text-${color} fw-bold`}>
                              {formatCurrency(totals[key]?.amount || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sales Table */}
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading sales data...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr className="table-light">
                        <th>Date</th>
                        <th>Customer</th>
                        <th>Mobile</th>
                        <th>Product</th>
                        <th className="text-end">Qty</th>
                        <th className="text-end">Price</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formattedSales.length > 0 ? (
                        formattedSales.map((sale) => {
                          const customerName =
                            sale.customer_name ||
                            sale.customer ||
                            sale.name ||
                            "-";
                          const customerMobile =
                            sale.customer_mobile ||
                            sale.mobile ||
                            sale.phone ||
                            sale.customerPhone ||
                            "-";
                          return (
                            <tr
                              key={`${sale.id}-${sale.sale_date}`}
                              className={sale.rowClass}
                            >
                              <td>
                                <div className="d-flex flex-column">
                                  <span>{sale.formattedDate}</span>
                                  <small className="text-muted">
                                    {sale.formattedTime}
                                  </small>
                                </div>
                              </td>
                              <td>{customerName}</td>
                              <td>{customerMobile}</td>
                              <td>{getProductName(sale.product_type)}</td>
                              <td className="text-end fw-bold">
                                {sale.quantity}
                              </td>
                              <td className="text-end">
                                {formatCurrency(sale.price)}
                              </td>
                              <td className="text-end fw-bold text-primary">
                                {formatCurrency(sale.total)}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-5">
                            <div className="text-muted">
                              <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                              No sales data found for the selected date range
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Header />
    </>
  );
}
